import React, { useEffect, useState } from "react";
import axios from 'axios';
import moment from 'moment';
import LessonDetails from "./modalComponents/LessonDetails";
import { Pencil, Trash2, CircleOff, PlusCircle, XCircle, MinusCircle, Users, FileText, Award, TrendingUp, TrendingDown, Minus } from "lucide-react";

export default function ClassManagement() {
  const [showForm, setShowForm] = useState(false);
  const [showMigrationForm, setShowMigrationForm] = useState(false);
  const [className, setClassName] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [courses, setCourses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [startingClassDate, setStartingClassDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const classesPerPage = 16;
  const [hideInactive, setHideInactive] = useState(false);

  // Manage classes required state
  const [showLessonsModal, setShowLessonsModal] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [liveLessons, setLiveLessons] = useState([]);
  const [selectedLiveLessonId, setSelectedLiveLessonId] = useState(null);
  const [singleLiveLesson, setSingleLiveLesson] = useState([]);
  const [showSingleLiveLessonModal, setShowSingleLiveLessonModal] = useState(false);
  const [showLessonAttendanceModal, setShowLessonAttendanceModal] = useState(false);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [classStudents, setClassStudents] = useState([]);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showRemoveStudentModal, setShowRemoveStudentModal] = useState(false);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [selectedAddStudents, setSelectedAddStudents] = useState([]);
  const [attendanceResults, setAttendanceResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);

  // Quiz logic
  const [showAddQuizModal, setShowAddQuizModal] = useState(false);
  const [quizzes, setQuizzes] = useState([]);
  const [selectedLessonId, setSelectedLessonId] = useState('');
  const [selectedQuizId, setSelectedQuizId] = useState('');
  const [loadingQuiz, setLoadingQuiz] = useState(false);
  const [showTestResultModal, setShowTestResultModal] = useState(false);
  const [selectedLessonIdForResults, setSelectedLessonIdForResults] = useState('');
  const [quizResults, setQuizResults] = useState([]);
  const [hasQuiz, setHasQuiz] = useState({});

  // Ranking modal state
  const [showRankingModal, setShowRankingModal] = useState(false);
  const [rankingData, setRankingData] = useState([]);

  const fetchQuizzes = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getQuizzes`);
      setQuizzes(response.data.data);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getCourse`);
      setCourses(response.data.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getStudent`);
      setStudents(response.data.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getClass`);
      setClasses(response.data.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  useEffect(() => {
    fetchCourses();
    fetchStudents();
    fetchClasses();
    fetchQuizzes();
  }, []);

  const handleCreateLiveClass = () => {
    setShowForm(true);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
    setCurrentPage(1);
  };

  const filteredClasses = classes.filter((c) => {
    const matchesSearch = `${c.class_id} ${c.className} ${c.course_id} ${c.class_start_date} ${c.class_end_date}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const isActive = c.status !== 'inactive';
    return hideInactive ? matchesSearch && isActive : matchesSearch;
  });

  const indexOfLast = currentPage * classesPerPage;
  const indexOfFirst = indexOfLast - classesPerPage;
  const currentClasses = filteredClasses.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredClasses.length / classesPerPage);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = {
      class_name: className,
      course: selectedCourse,
      students: selectedStudents.map(student => student.student_id),
      starting_date: startingClassDate,
      startTime: startTime,
      endTime: endTime,
    };
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/createClass`, formData);
      if (response.status === 200) {
        setShowForm(false);
        setClassName('');
        setSelectedCourse('');
        setSelectedStudents([]);
        setStartingClassDate('');
        setStartTime('');
        setEndTime('');
        await fetchClasses();
      }
    } catch (error) {
      console.error('Error creating course:', error);
      alert('Failed to create course.');
      setClassName('');
      setSelectedCourse('');
      setSelectedStudents([]);
      setStartingClassDate('');
      setStartTime('');
      setEndTime('');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setSelectedStudents([]);
  };

  const addStudent = (student) => {
    if (!selectedStudents.some((s) => s.student_id === student.student_id)) {
      setSelectedStudents([...selectedStudents, student]);
    }
  };

  const removeStudent = (studentId) => {
    setSelectedStudents(selectedStudents.filter(student => student.student_id !== studentId));
  };

  const filteredStudents = students.filter((student) =>
    `${student.surname} ${student.first_name} `
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const handleManageLessons = async (classId) => {
    setSelectedClassId(classId);
    setShowLessonsModal(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getLiveLesson/${classId}`);
      if (response.data.success) {
        setLiveLessons(response.data.data);
      } else {
        setLiveLessons([]);
      }
    } catch (error) {
      console.error('Error fetching lessons:', error);
      setLiveLessons([]);
    }
  };

  const handleManageSingleLiveLesson = async (liveLessonId) => {
    setSelectedLiveLessonId(liveLessonId);
    setShowSingleLiveLessonModal(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getSingleLiveLesson/${liveLessonId}`);
      if (response.data.success) {
        setSingleLiveLesson(response.data.data);
      } else {
        setSingleLiveLesson([]);
      }
    } catch (error) {
      console.error('Error fetching lessons:', error);
    }
  };

  const handleManageLessonAttendance = async (liveLessonId) => {
    setSelectedLiveLessonId(liveLessonId);
    setShowLessonAttendanceModal(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getLessonAttendance/${liveLessonId}`);
      if (response.data.success) {
        setAttendanceResults(response.data.data);
      } else {
        setAttendanceResults([]);
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
      alert('Failed to fetch attendance data.');
      setAttendanceResults([]);
    }
  };

  const handleToggleActive = async (classId) => {
    await axios.patch(`${process.env.REACT_APP_API_URL}/admin/toggleClassStatus/${classId}`);
    fetchClasses();
  };

  const handleDelete = async (classId) => {
    if (!window.confirm(`Are you sure you want to delete this class "${classId}"?`)) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/admin/deleteClass/${classId}`, {
        data: { classId }
      });
      const updateClasses = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getClass`);
      setClasses(updateClasses.data.data);
      alert("Class deleted successfully.");
    } catch (error) {
      console.error("Error deleting class:", error);
      alert("Failed to delete Class.");
    }
  };

  const handleMigrateLiveClass = () => {
    setShowMigrationForm(true);
  };

  const handleMigrationSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/migrateClass`, {
        className,
        course_id: selectedCourse,
        source_class_id: selectedClass,
        startingClassDate,
        startTime,
        endTime,
      });
      console.log("Class migration successful:", response.data);
      setShowMigrationForm(false);
      fetchClasses();
    } catch (error) {
      console.error("Error migrating class:", error);
    }
  };

  const handleMigrationCancel = () => {
    setShowMigrationForm(false);
    setClassName("");
    setSelectedCourse("");
    setSelectedClass("");
    setSearchQuery("");
    setStartingClassDate("");
    setStartTime("");
    setEndTime("");
  };

  const fetchLiveLessons = async (classId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getLiveLessons?class_id=${classId}`);
      setLiveLessons(response.data.data);
    } catch (error) {
      console.error('Error fetching live lessons:', error);
    }
  };

  const handleAddLiveLesson = async () => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/addLiveLesson`, {
        class_id: selectedClassId,
      });
      if (response.data.success) {
        await fetchLiveLessons(selectedClassId);
        setShowLessonsModal(false);
        alert('Live lesson added successfully.');
      } else {
        alert(response.data.message || 'Failed to add live lesson.');
      }
    } catch (error) {
      console.error('Error adding live lesson:', error);
      alert('An error occurred while adding the live lesson.');
    }
  };

  const handleDeleteLiveLesson = async () => {
    if (!window.confirm('Are you sure you want to delete the last live lesson?')) {
      return;
    }
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/deleteLiveLesson`, {
        class_id: selectedClassId,
      });
      if (response.data.success) {
        await fetchLiveLessons(selectedClassId);
        setShowLessonsModal(false);
        alert('Live lesson deleted successfully.');
      } else {
        alert(response.data.message || 'Failed to delete live lesson.');
      }
    } catch (error) {
      console.error('Error deleting live lesson:', error);
      alert('An error occurred while deleting the live lesson.');
    }
  };

  const fetchClassStudents = async (classId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getClassStudents?class_id=${classId}`);
      setClassStudents(response.data.data);
    } catch (error) {
      console.error('Error fetching class students:', error);
      setClassStudents([]);
    }
  };

  const handleViewStudents = (classId) => {
    setSelectedClassId(classId);
    fetchClassStudents(classId);
    setShowStudentsModal(true);
  };

  const fetchAvailableStudents = async (classId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getAvailableStudents?class_id=${classId}`);
      setAvailableStudents(response.data.data);
    } catch (error) {
      console.error('Error fetching available students:', error);
      setAvailableStudents([]);
    }
  };

  const handleStudentSelect = (studentId) => {
    setSelectedAddStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleAddStudent = () => {
    fetchAvailableStudents(selectedClassId);
    setSelectedAddStudents([]);
    setSearchTerm('');
    setShowAddStudentModal(true);
  };

  const handleRemoveStudent = () => {
    setSelectedAddStudents([]);
    setShowRemoveStudentModal(true);
  };

  const handleConfirmAddStudents = async () => {
    if (selectedAddStudents.length === 0) {
      alert('Please select at least one student.');
      return;
    }
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/addStudentsToClass`, {
        class_id: selectedClassId,
        student_ids: selectedAddStudents,
      });
      if (response.data.success) {
        await fetchClassStudents(selectedClassId);
        setShowAddStudentModal(false);
        setShowStudentsModal(false);
        alert('Students added successfully.');
      } else {
        alert(response.data.message || 'Failed to add students.');
      }
    } catch (error) {
      console.error('Error adding students:', error);
      alert('An error occurred while adding students.');
    }
  };

  const handleConfirmRemoveStudents = async () => {
    if (selectedAddStudents.length === 0) {
      alert('Please select at least one student.');
      return;
    }
    if (!window.confirm('Are you sure you want to remove the selected students?')) {
      return;
    }
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/removeStudentsFromClass`, {
        class_id: selectedClassId,
        student_ids: selectedAddStudents,
      });
      if (response.data.success) {
        await fetchClassStudents(selectedClassId);
        setShowRemoveStudentModal(false);
        setShowStudentsModal(false);
        alert('Students removed successfully.');
      } else {
        alert(response.data.message || 'Failed to remove students.');
      }
    } catch (error) {
      console.error('Error removing students:', error);
      alert('An error occurred while removing students.');
    }
  };

  const handleDeleteThisLiveLesson = async (liveLessonId) => {
    if (!window.confirm(`Are you sure you want to delete lesson "${liveLessonId}"?\nThis will also renumber subsequent lessons.`)) {
      return;
    }
  
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/delete_this_live_lesson`, {
        live_lesson_id: liveLessonId,
      });
  
      if (response.data.success) {
        // Refresh the live lessons list
        await fetchLiveLessons(selectedClassId);
  
        // Close any open sub-modals that depend on a specific live_lesson_id
        setShowSingleLiveLessonModal(false);
        setShowLessonAttendanceModal(false);
        setShowTestResultModal(false);
  
        // Clear related state
        setSelectedLiveLessonId(null);
        setSingleLiveLesson([]);
        setAttendanceResults([]);
        setQuizResults([]);
        setSelectedLessonIdForResults('');
  
        // Optionally close the main lessons modal for clean UX
        // (Recommended: prevents confusion with outdated IDs)
        setShowLessonsModal(false);
  
        alert('Live lesson deleted and subsequent lessons renumbered successfully.');
      } else {
        alert(response.data.message || 'Failed to delete the live lesson.');
      }
    } catch (error) {
      console.error('Error deleting specific live lesson:', error);
      alert(error.response?.data?.message || 'An error occurred while deleting the live lesson.');
    }
  };

  useEffect(() => {
    if (showLessonsModal && liveLessons.length > 0) {
      const checkQuizzes = async () => {
        try {
          const lessonIds = liveLessons.map(lesson => lesson.live_lesson_id);
          const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/checkLessonQuizzes`, { lesson_ids: lessonIds });
          setHasQuiz(response.data.data);
        } catch (error) {
          console.error('Error checking lesson quizzes:', error);
        }
      };
      checkQuizzes();
    }
  }, [showLessonsModal, liveLessons]);

  const filteredAvailableStudents = availableStudents.filter((student) =>
    `${student.surname} ${student.first_name}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  // Toggle attendance between 'pending', 'presence', 'absence'
  const toggleAttendance = (index) => {
    const newResults = [...attendanceResults];
    const current = newResults[index].attendance;
    newResults[index].attendance =
      current === 'pending' ? 'presence' :
      current === 'presence' ? 'absence' :
      current === 'absence' ? 'leave' :
      'pending';  // Back to pending if 'leave'
    setAttendanceResults(newResults);
  };

  // Toggle homework_status between 0, 1, 2
  const toggleHomeworkStatus = (index) => {
    const newResults = [...attendanceResults];
    const current = newResults[index].homework_status;
    newResults[index].homework_status = current === 0 ? 1 : current === 1 ? 2 : 0;
    setAttendanceResults(newResults);
  };

  // Render visual indicator for attendance
  const renderAttendanceIndicator = (attendance) => {
    if (attendance === 'presence') return '✓';
    if (attendance === 'absence') return '✗';
    if (attendance === 'leave') return 'L';
    return '';  // For 'pending'
  };


  // Render visual indicator for homework_status
  const renderHomeworkIndicator = (status) => {
    if (status === 1) return '✓';
    if (status === 2) return '✗';
    return '';
  };

  // Get student name by student_id
  const getStudentName = (studentId) => {
    const student = students.find(s => s.student_id === studentId);
    return student ? `${student.surname} ${student.first_name}` : 'Unknown';
  };

  // Get quiz full mark by quiz_name
  const getQuizFullMark = (quizName) => {
    const quiz = quizzes.find(q => q.quiz_name === quizName);
    return quiz ? quiz.full_mark : 'N/A';
  };

  // Get result display format (e.g., "12/25" or "-/25")
  const getResultDisplay = (result, quizName) => {
    const fullMark = getQuizFullMark(quizName);
    if (fullMark === 'N/A') return 'N/A';
    return result.student_result ? `${result.student_result}/${fullMark}` : `-/${fullMark}`;
  };

  // Check if result is less than 50% of full mark
  const isLowScore = (result, quizName) => {
    const fullMark = getQuizFullMark(quizName);
    if (fullMark === 'N/A' || !result.student_result) return false;
    return parseFloat(result.student_result) < 0.5 * parseFloat(fullMark);
  };

  // Sort quiz results by student_result (descending, empty at bottom)
  const sortedQuizResults = quizResults
    .map((result, index) => ({ ...result, originalIndex: index }))
    .sort((a, b) => {
      const aResult = a.student_result ? parseFloat(a.student_result) : -Infinity;
      const bResult = b.student_result ? parseFloat(b.student_result) : -Infinity;
      return bResult - aResult;
    })
    .map((result, index) => ({
      ...result,
      rank: result.student_result ? index + 1 : '-'
    }));

  // Handle view ranking
  const handleViewRanking = async (classId) => {
    setSelectedClassId(classId);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getClassQuizData?class_id=${classId}`);
      if (response.data.success) {
        setRankingData(response.data.data);
        setShowRankingModal(true);
      } else {
        alert('Failed to fetch ranking data.');
      }
    } catch (error) {
      console.error('Error fetching ranking data:', error);
      alert('Failed to fetch ranking data.');
    }
  };

  // Sort ranking data by recent_ranking_score descending
  const sortedRankingData = rankingData
    .filter(student => student.recent_ranking_score !== undefined)
    .sort((a, b) => b.recent_ranking_score - a.recent_ranking_score);

  return (
    <div className="p-8 min-h-screen overflow-y-auto">
      <h1 className="text-2xl font-bold">Live Class Management</h1>
      <div className="mt-2 mb-4 border-t border-gray-400 w-9/12 mx-auto"></div>
      {/* Search Bar */}
      <div className="mb-4 flex flex-col md:flex-row md:items-center md:gap-4">
        <input
          type="text"
          placeholder="Search by name, ID or Date"
          value={searchQuery}
          onChange={handleSearch}
          className="w-full md:w-1/3 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <label className="flex items-center mt-2 md:mt-0">
          <input
            type="checkbox"
            checked={hideInactive}
            onChange={(e) => setHideInactive(e.target.checked)}
            className="mr-2"
          />
          Hide Inactive Classes
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={handleCreateLiveClass}
          className="px-4 py-2 bg-red-400 text-white rounded hover:bg-red-500"
        >
          Create Class
        </button>
        <button
          onClick={handleMigrateLiveClass}
          className="px-4 py-2 bg-green-400 text-white rounded hover:bg-green-500"
        >
          Migrate Class
        </button>
      </div>
      {showForm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white w-1/2 max-w-lg rounded-lg shadow-lg p-6 relative">
            <h2 className="text-xl font-semibold mb-4">Create a New Live Class</h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="classname">Live Class Name</label>
                <input
                  type="text"
                  id="classname"
                  value={className}
                  onChange={(e) => setClassName(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="course">Course</label>
                <select
                  id="course"
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a Course</option>
                  {courses.map((course) => (
                    <option key={course.course_id} value={course.course_id}>
                      {course.course_name} ({course.course_id})
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="studentSearch">Search Students</label>
                <input
                  type="text"
                  id="studentSearch"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Type to search for students..."
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Students</label>
                <div className="h-32 overflow-y-auto border rounded p-2">
                  {filteredStudents.map((student) => (
                    <div key={student.student_id} className="flex items-center justify-between py-1">
                      <span>{`${student.surname} ${student.first_name} (${student.student_id})`}</span>
                      <button
                        type="button"
                        onClick={() => addStudent(student)}
                        className="bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                      >
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Selected Students</label>
                <div className="border rounded p-2 h-32 overflow-y-auto">
                  {selectedStudents.length > 0 ? selectedStudents.map((student) => (
                    <div key={student.student_id} className="flex justify-between items-center mb-2">
                      <span>{`${student.surname} ${student.first_name} (${student.student_id})`}</span>
                      <button
                        type="button"
                        onClick={() => removeStudent(student.student_id)}
                        className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  )) : <p>No students selected.</p>}
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="startingClassDate">Starting Class Date</label>
                <input
                  type="date"
                  id="startingClassDate"
                  value={startingClassDate}
                  onChange={(e) => setStartingClassDate(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="startTime">Start Time</label>
                <input
                  type="time"
                  id="startTime"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="endTime">End Time</label>
                <input
                  type="time"
                  id="endTime"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="bg-gray-500 text-white px-4 py-2 mr-2 rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showMigrationForm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white w-1/2 max-w-lg rounded-lg shadow-lg p-6 relative">
            <h2 className="text-xl font-semibold mb-4">Migrate an Existing Live Class</h2>
            <form onSubmit={handleMigrationSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="classname">
                  Live Class Name
                </label>
                <input
                  type="text"
                  id="classname"
                  value={className}
                  onChange={(e) => setClassName(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="course">
                  Course
                </label>
                <select
                  id="course"
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a Course</option>
                  {courses.map((course) => (
                    <option key={course.course_id} value={course.course_id}>
                      {course.course_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="classSearch">
                  Search Classes
                </label>
                <input
                  type="text"
                  id="classSearch"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Type to filtering classes..."
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="sourceClass">
                  Source Class
                </label>
                <select
                  id="sourceClass"
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a Class</option>
                  {filteredClasses.map((cls) => (
                    <option key={cls.class_id} value={cls.class_id}>
                      {`${cls.class_name} (ID: ${cls.class_id})`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="startingClassDate">
                  Starting Class Date
                </label>
                <input
                  type="date"
                  id="startingClassDate"
                  value={startingClassDate}
                  onChange={(e) => setStartingClassDate(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="startTime">
                  Start Time
                </label>
                <input
                  type="time"
                  id="startTime"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="endTime">
                  End Time
                </label>
                <input
                  type="time"
                  id="endTime"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleMigrationCancel}
                  className="bg-gray-500 text-white px-4 py-2 mr-2 rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <table className="w-full border-collapse bg-white">
        <thead>
          <tr className="bg-gray-200">
            {/* <th className="border px-4 py-2">Class ID</th> */}
            <th className="border px-4 py-2">Course ID</th>
            <th className="border px-4 py-2">Class Name</th>
            <th className="border px-4 py-2">Class start date</th>
            <th className="border px-4 py-2">Class End date</th>
            <th className="border px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentClasses.map((classes) => (
            <tr key={classes.class_id} className="text-center border-t">
              {/* <td className="px-4 py-2 border">{classes.class_id}</td> */}
              <td className="px-4 py-2 border">{classes.course_id}</td>
              <td className="px-4 py-2 border">{classes.class_name}</td>
              <td className="px-4 py-2 border">
                {moment(classes.class_start_date).format('YYYY-MM-DD')}
              </td>
              <td className="px-4 py-2 border">
                {moment(classes.class_end_date).format('YYYY-MM-DD')}
              </td>
              <td className="px-4 py-2 border">
                <div className="flex justify-center gap-2">
                  <button
                    onClick={() => handleManageLessons(classes.class_id)}
                  >
                    <Pencil className="w-5 h-5 text-blue-600 hover:text-blue-800" />
                  </button>
                  <button
                    onClick={() => handleDelete(classes.class_id)}
                  >
                    <Trash2 className="w-5 h-5 text-red-600 hover:text-red-800" />
                  </button>
                  <button onClick={() => handleToggleActive(classes.class_id)}>
                    <CircleOff
                      className={`w-5 h-5 ${
                        classes.status === 'active' ? "text-green-600" : "text-gray-400"
                      } hover:text-green-800`}
                    />
                  </button>
                  <button onClick={() => handleViewStudents(classes.class_id)}>
                    <Users className="w-5 h-5 text-purple-600 hover:text-purple-800" />
                  </button>
                  <button onClick={() => handleViewRanking(classes.class_id)}>
                    <Award className="w-5 h-5 text-yellow-600 hover:text-yellow-800" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {showLessonsModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-3/4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Manage Lessons for Class ID: {selectedClassId}</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAddQuizModal(true)}
                  className="bg-blue-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-blue-600"
                >
                  <PlusCircle className="w-5 h-5" />
                  Add Quiz
                </button>
                <button
                  onClick={handleAddLiveLesson}
                  className="bg-green-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-green-600"
                >
                  <PlusCircle className="w-5 h-5" />
                  Add a Lesson
                </button>
                <button
                  onClick={handleDeleteLiveLesson}
                  className="bg-red-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-red-600"
                >
                  <MinusCircle className="w-5 h-5" />
                  Delete Last Lesson
                </button>
              </div>
            </div>
            <table className="min-w-full bg-white border border-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 border">Live Lesson ID</th>
                  <th className="px-4 py-2 border">Lesson date</th>
                  <th className="px-4 py-2 border">Class Start Time</th>
                  <th className="px-4 py-2 border">Class End Time</th>
                  <th className="px-4 py-2 border">Actions</th>
                </tr>
              </thead>
              <tbody>
                {liveLessons.map((lesson) => (
                  <tr key={lesson.live_lesson_id}>
                    <td className="px-4 py-2 border">{lesson.live_lesson_id}</td>
                    <td className="px-4 py-2 border">{lesson.lesson_date}</td>
                    <td className="px-4 py-2 border">{lesson.start_time}</td>
                    <td className="px-4 py-2 border">{lesson.end_time}</td>
                    <td className="px-4 py-2 border space-x-2">
                      {hasQuiz[lesson.live_lesson_id] && (
                        <button
                          onClick={async () => {
                            setSelectedLessonIdForResults(lesson.live_lesson_id);
                            setLoadingResults(true);
                            try {
                              const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getQuizResults?lesson_id=${lesson.live_lesson_id}`);
                              setQuizResults(response.data.data.map(result => ({
                                ...result,
                                attendance: result.attendance === 'presence',
                              })));
                              setShowTestResultModal(true);
                            } catch (error) {
                              console.error('Error fetching quiz results:', error);
                              alert('Failed to fetch quiz results.');
                            } finally {
                              setLoadingResults(false);
                            }
                          }}
                          className="text-green-600 hover:text-green-800"
                        >
                          <FileText className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => handleManageSingleLiveLesson(lesson.live_lesson_id)}
                      >
                        <Pencil className="w-5 h-5 text-blue-600 hover:text-blue-800" />
                      </button>
                      <button
                        onClick={() => handleManageLessonAttendance(lesson.live_lesson_id)}
                      >
                        <Users className="w-5 h-5 text-purple-600 hover:text-purple-800" />
                      </button>
                      {/* New Delete Specific Lesson Button */}
                      <button
                        onClick={() => handleDeleteThisLiveLesson(lesson.live_lesson_id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              onClick={() => setShowLessonsModal(false)}
              className="mt-4 bg-gray-500 text-white px-4 py-2 rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}
      {showSingleLiveLessonModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-1/2">
            <h3 className="text-xl font-semibold mb-4">Manage Lesson for Lesson ID: {selectedLiveLessonId}</h3>
            {singleLiveLesson ? (
              <LessonDetails lesson={singleLiveLesson} onClose={() => setShowSingleLiveLessonModal(false)} />
            ) : (
              <p>Loading lesson data...</p>
            )}
          </div>
        </div>
      )}
      {showStudentsModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-3/4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-xl font-semibold flex items-center gap-3 flex-wrap">
                Students in Class
                <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-gray-800 rounded-full text-base font-medium">
                  {/* {selectedClassId} */}
                  {(() => {
                    const cls = classes.find(c => c.class_id === selectedClassId);
                    return cls ? (
                      <span className=" text-gray-800 rounded-full text-base font-medium">{cls.class_name}</span>
                    ) : null;
                  })()}
                </span>
              </h3>
                <span className="text-gray-600">
                  students: <strong className="text-gray-900">{classStudents.length}</strong>
                </span>
              <div className="flex gap-2">
                <button
                  onClick={handleAddStudent}
                  className="bg-green-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-green-600"
                >
                  <PlusCircle className="w-5 h-5" />
                  Add Student
                </button>
                <button
                  onClick={handleRemoveStudent}
                  className="bg-red-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-red-600"
                >
                  <MinusCircle className="w-5 h-5" />
                  Remove Student
                </button>
              </div>
            </div>
            <table className="min-w-full bg-white border border-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 border">Student ID</th>
                  <th className="px-4 py-2 border">Surname</th>
                  <th className="px-4 py-2 border">First Name</th>
                  <th className="px-4 py-2 border">Chinese Name</th>
                </tr>
              </thead>
              <tbody>
                {classStudents.length > 0 ? (
                  classStudents.map((student) => (
                    <tr key={student.student_id}>
                      <td className="px-4 py-2 border">{student.student_id}</td>
                      <td className="px-4 py-2 border">{student.surname}</td>
                      <td className="px-4 py-2 border">{student.first_name}</td>
                      <td className="px-4 py-2 border">{student.chinese_name || 'N/A'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-4 py-2 border text-center text-gray-600">
                      No students enrolled in this class.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <button
              onClick={() => setShowStudentsModal(false)}
              className="mt-4 bg-gray-500 text-white px-4 py-2 rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}
      {showAddStudentModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-3/4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Add Students to Class ID: {selectedClassId}</h3>
              <div className="flex gap-2">
                <button
                  onClick={handleConfirmAddStudents}
                  className="bg-green-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-green-600"
                >
                  <PlusCircle className="w-5 h-5" />
                  Confirm Add
                </button>
                <button
                  onClick={() => setShowAddStudentModal(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search students by name..."
              className="mb-4 p-2 w-full border rounded"
            />
            <table className="min-w-full bg-white border border-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 border">Select</th>
                  <th className="px-4 py-2 border">Student ID</th>
                  <th className="px-4 py-2 border">Surname</th>
                  <th className="px-4 py-2 border">First Name</th>
                  <th className="px-4 py-2 border">Chinese Name</th>
                </tr>
              </thead>
              <tbody>
                {filteredAvailableStudents.length > 0 ? (
                  filteredAvailableStudents.map((student) => (
                    <tr key={student.student_id}>
                      <td className="px-4 py-2 border text-center">
                        <input
                          type="checkbox"
                          checked={selectedAddStudents.includes(student.student_id)}
                          onChange={() => handleStudentSelect(student.student_id)}
                        />
                      </td>
                      <td className="px-4 py-2 border">{student.student_id}</td>
                      <td className="px-4 py-2 border">{student.surname}</td>
                      <td className="px-4 py-2 border">{student.first_name}</td>
                      <td className="px-4 py-2 border">{student.chinese_name || 'N/A'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-4 py-2 border text-center text-gray-600">
                      No available students found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {showRemoveStudentModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-3/4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Remove Students from Class ID: {selectedClassId}</h3>
              <div className="flex gap-2">
                <button
                  onClick={handleConfirmRemoveStudents}
                  className="bg-red-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-red-600"
                >
                  <MinusCircle className="w-5 h-5" />
                  Confirm Remove
                </button>
                <button
                  onClick={() => setShowRemoveStudentModal(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
            <table className="min-w-full bg-white border border-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 border">Select</th>
                  <th className="px-4 py-2 border">Student ID</th>
                  <th className="px-4 py-2 border">Surname</th>
                  <th className="px-4 py-2 border">First Name</th>
                  <th className="px-4 py-2 border">Chinese Name</th>
                </tr>
              </thead>
              <tbody>
                {classStudents.length > 0 ? (
                  classStudents.map((student) => (
                    <tr key={student.student_id}>
                      <td className="px-4 py-2 border text-center">
                        <input
                          type="checkbox"
                          checked={selectedAddStudents.includes(student.student_id)}
                          onChange={() => handleStudentSelect(student.student_id)}
                        />
                      </td>
                      <td className="px-4 py-2 border">{student.student_id}</td>
                      <td className="px-4 py-2 border">{student.surname}</td>
                      <td className="px-4 py-2 border">{student.first_name}</td>
                      <td className="px-4 py-2 border">{student.chinese_name || 'N/A'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-4 py-2 border text-center text-gray-600">
                      No students enrolled in this class.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {showAddQuizModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg w-3/4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Create Quiz for Class ID: {selectedClassId}</h3>
              <button
                onClick={() => setShowAddQuizModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!selectedLessonId || !selectedQuizId) {
                alert('Please select both a lesson and a quiz.');
                return;
              }
              const assignedLessonId = selectedLessonId;
              setLoadingQuiz(true);
              try {
                await axios.post(`${process.env.REACT_APP_API_URL}/admin/assignQuizToLesson`, {
                  class_id: selectedClassId,
                  lesson_id: selectedLessonId,
                  quiz_name: selectedQuizId,
                });
                alert('Quiz assigned successfully.');
                setHasQuiz(prev => ({ ...prev, [assignedLessonId]: true }));
                setShowAddQuizModal(false);
                setSelectedLessonId('');
                setSelectedQuizId('');
              } catch (error) {
                console.error('Error assigning quiz:', error);
                alert(error.response?.data?.error || 'Failed to assign quiz.');
              } finally {
                setLoadingQuiz(false);
              }
            }}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-1">Select Lesson</label>
                <select
                  value={selectedLessonId}
                  onChange={(e) => setSelectedLessonId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a lesson</option>
                  {liveLessons.map((lesson) => (
                    <option key={lesson.live_lesson_id} value={lesson.live_lesson_id}>
                      {lesson.live_lesson_id} ({lesson.start_time})
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-1">Select Quiz</label>
                <select
                  value={selectedQuizId}
                  onChange={(e) => setSelectedQuizId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a quiz</option>
                  {quizzes.map((quiz) => (
                    <option key={quiz.quiz_name} value={quiz.quiz_name}>
                      {quiz.quiz_name} (Full Mark: {quiz.full_mark})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddQuizModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 ${loadingQuiz && "opacity-50 cursor-not-allowed"}`}
                  disabled={loadingQuiz}
                >
                  {loadingQuiz ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showTestResultModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg w-11/12 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Manage Test Results for Lesson ID: {selectedLessonIdForResults}</h3>
              <button
                onClick={() => setShowTestResultModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              setLoadingResults(true);
              try {
                await axios.post(`${process.env.REACT_APP_API_URL}/admin/updateQuizResults`, {
                  lesson_id: selectedLessonIdForResults,
                  results: quizResults.map(result => ({
                    quiz_name: result.quiz_name,
                    student_id: result.student_id,
                    student_result: result.student_result || null,
                    attendance: result.attendance ? 'presence' : 'absence',
                  })),
                });
                alert('Quiz results updated successfully.');
                setShowTestResultModal(false);
                setQuizResults([]);
                setSelectedLessonIdForResults('');
              } catch (error) {
                console.error('Error updating quiz results:', error);
                alert(error.response?.data?.error || 'Failed to update quiz results.');
              } finally {
                setLoadingResults(false);
              }
            }}>
              <div className="overflow-x-auto overflow-y-auto max-h-120">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead>
                    <tr className="bg-blue-100 sticky top-0">
                      <th className="px-4 py-2 border">Rank</th>
                      <th className="px-4 py-2 border">Quiz Name</th>
                      <th className="px-4 py-2 border">Student</th>
                      <th className="px-4 py-2 border">Student Result</th>
                      <th className="px-4 py-2 border">Result/Full Mark</th>
                      <th className="px-4 py-2 border">Attendance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedQuizResults.map((result, index) => (
                      <tr key={`${result.quiz_name}-${result.student_id}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-2 border">{result.rank}</td>
                        <td className="px-4 py-2 border">{result.quiz_name}</td>
                        <td className="px-4 py-2 border">{`${result.student_id} (${getStudentName(result.student_id)})`}</td>
                        <td className="px-4 py-2 border">
                          <input
                            type="text"
                            value={result.student_result || ''}
                            onChange={(e) => {
                              const newResults = [...quizResults];
                              newResults[result.originalIndex].student_result = e.target.value;
                              setQuizResults(newResults);
                            }}
                            className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className={`px-4 py-2 border ${isLowScore(result, result.quiz_name) ? 'text-red-600' : ''}`}>
                          {getResultDisplay(result, result.quiz_name)}
                        </td>
                        <td className="px-4 py-2 border text-center">
                          <input
                            type="checkbox"
                            checked={result.attendance}
                            onChange={(e) => {
                              const newResults = [...quizResults];
                              newResults[result.originalIndex].attendance = e.target.checked;
                              setQuizResults(newResults);
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setShowTestResultModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 ${loadingResults && "opacity-50 cursor-not-allowed"}`}
                  disabled={loadingResults}
                >
                  {loadingResults ? "Updating..." : "Confirm"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showLessonAttendanceModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg w-3/4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Manage Attendance for Lesson ID: {selectedLiveLessonId}</h3>
              <button
                onClick={() => setShowLessonAttendanceModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setLoadingResults(true);
                try {
                  await axios.post(`${process.env.REACT_APP_API_URL}/admin/updateAttendanceRecord`, {
                    live_lesson_id: selectedLiveLessonId,
                    results: attendanceResults.map((result) => ({
                      student_id: result.student_id,
                      attendance: result.attendance,
                      homework_status: result.homework_status,
                    })),
                  });
                  alert('Attendance updated successfully.');
                  setShowLessonAttendanceModal(false);
                  setAttendanceResults([]);
                  setSelectedLiveLessonId('');
                } catch (error) {
                  console.error('Error updating attendance:', error);
                  alert(error.response?.data?.error || 'Failed to update attendance.');
                } finally {
                  setLoadingResults(false);
                }
              }}
            >
              <div className="overflow-x-auto max-h-96">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead>
                    <tr className="bg-blue-100 sticky top-0">
                      <th className="px-4 py-2 border">Student</th>
                      <th className="px-4 py-2 border">Attendance</th>
                      <th className="px-4 py-2 border">Homework Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceResults.map((result, index) => (
                      <tr
                        key={`${result.student_id}-${index}`}
                        className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                      >
                        <td className="px-4 py-2 border">{`${result.student_id} (${getStudentName(result.student_id)})`}</td>
                        <td className="px-4 py-2 border text-center">
                          <button
                            type="button"
                            onClick={() => toggleAttendance(index)}
                            className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {renderAttendanceIndicator(result.attendance)}
                          </button>
                        </td>
                        <td className="px-4 py-2 border text-center">
                          <button
                            type="button"
                            onClick={() => toggleHomeworkStatus(index)}
                            className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {renderHomeworkIndicator(result.homework_status)}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setShowLessonAttendanceModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 ${
                    loadingResults && 'opacity-50 cursor-not-allowed'
                  }`}
                  disabled={loadingResults}
                >
                  {loadingResults ? 'Updating...' : 'Confirm'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showRankingModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg w-11/12 max-h-[90vh] overflow-y-auto relative">
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-white z-10 pb-2 border-b">
              <h3 className="text-xl font-semibold">
                Student Ranking for Class ID: {selectedClassId}
              </h3>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    // Copy logic
                    const headers = [
                      "Student Name", "Student ID", "Latest %", "2nd Latest %", "3rd Latest %",
                      "Recent Score", "Previous Score", "Recent Rank", "Previous Rank", "Change", "Trend"
                    ];

                    const rows = rankingData
                      .filter(s => s.recent_ranking_score !== null)
                      .sort((a, b) => (a.recent_rank ?? Infinity) - (b.recent_rank ?? Infinity))
                      .map(student => {
                        const change = student.rank_change;
                        const changeText = change !== null 
                          ? (change > 0 ? `+${change}` : change) 
                          : '-';
                        const trendText = change > 0 ? 'Up' : change < 0 ? 'Down' : 'Same';

                        return [
                          student.student_name,
                          student.student_id,
                          student.latest_percentage?.toFixed(1) ?? '-',
                          student.second_latest_percentage?.toFixed(1) ?? '-',
                          student.third_latest_percentage?.toFixed(1) ?? '-',
                          student.recent_ranking_score?.toFixed(1) ?? '-',
                          student.previous_ranking_score?.toFixed(1) ?? '-',
                          student.recent_rank ?? '-',
                          student.previous_rank ?? '-',
                          changeText,
                          trendText
                        ].join('\t');
                      });

                    const tsvContent = [headers.join('\t'), ...rows].join('\n');

                    navigator.clipboard.writeText(tsvContent).then(() => {
                      alert('Ranking table copied to clipboard! Paste into Excel or Google Sheets.');
                    }).catch(err => {
                      console.error('Failed to copy:', err);
                      alert('Failed to copy to clipboard.');
                    });
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                    <path d="M6 8a2 2 0 012-2h5a2 2 0 012 2v7a2 2 0 01-2 2H8a2 2 0 01-2-2V8z" />
                    <path d="M4 4h10a1 1 0 011 1v1H3V5a1 1 0 011-1z" />
                  </svg>
                  Copy Table
                </button>

                <button
                  onClick={() => {
                    setShowRankingModal(false);
                    setRankingData([]);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            {rankingData.filter(s => s.recent_ranking_score !== null).length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead>
                    <tr className="bg-yellow-100 sticky top-0">
                      <th className="px-4 py-2 border">Student Name</th>
                      <th className="px-4 py-2 border">Student ID</th>
                      <th className="px-4 py-2 border">Latest %</th>
                      <th className="px-4 py-2 border">2nd Latest %</th>
                      <th className="px-4 py-2 border">3rd Latest %</th>
                      <th className="px-4 py-2 border">Recent Score</th>
                      <th className="px-4 py-2 border">Previous Score</th>
                      <th className="px-4 py-2 border">Recent Rank</th>
                      <th className="px-4 py-2 border">Previous Rank</th>
                      <th className="px-4 py-2 border">Change</th>
                      <th className="px-4 py-2 border">Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankingData
                      .filter(s => s.recent_ranking_score !== null)
                      .sort((a, b) => (a.recent_rank ?? Infinity) - (b.recent_rank ?? Infinity))
                      .map((student, idx) => {
                        const change = student.rank_change;
                        const isImprove = change > 0;
                        const isDecline = change < 0;

                        return (
                          <tr key={student.student_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-2 border">{student.student_name}</td>
                            <td className="px-4 py-2 border">{student.student_id}</td>
                            <td className="px-4 py-2 border">{student.latest_percentage?.toFixed(1) ?? '-'}</td>
                            <td className="px-4 py-2 border">{student.second_latest_percentage?.toFixed(1) ?? '-'}</td>
                            <td className="px-4 py-2 border">{student.third_latest_percentage?.toFixed(1) ?? '-'}</td>
                            <td className="px-4 py-2 border">{student.recent_ranking_score?.toFixed(1) ?? '-'}</td>
                            <td className="px-4 py-2 border">{student.previous_ranking_score?.toFixed(1) ?? '-'}</td>
                            <td className="px-4 py-2 border font-bold text-lg">{student.recent_rank ?? '-'}</td>
                            <td className="px-4 py-2 border">{student.previous_rank ?? '-'}</td>
                            <td className={`px-4 py-2 border font-bold ${isImprove ? 'text-green-600' : isDecline ? 'text-red-600' : ''}`}>
                              {change !== null ? (change > 0 ? `+${change}` : change) : '-'}
                            </td>
                            <td className="px-4 py-2 border text-center">
                              {isImprove && <TrendingUp className="w-6 h-6 text-green-600 mx-auto" />}
                              {isDecline && <TrendingDown className="w-6 h-6 text-red-600 mx-auto" />}
                              {(!isImprove && !isDecline) && <Minus className="w-6 h-6 text-gray-400 mx-auto" />}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-gray-600 py-10">No ranking data available for this class.</p>
            )}
          </div>
        </div>
      )}
      <div className="flex justify-between items-center mt-6">
        <button
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <select
          value={currentPage}
          onChange={(e) => setCurrentPage(Number(e.target.value))}
          className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
            <option key={page} value={page}>
              Page {page} of {totalPages}
            </option>
          ))}
        </select>
        <button
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
}